import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useTopbarIsland } from "@/features/workbench/topbar-island-context";
import { useI18n } from "@/locales/i18n-provider";

type VersionManifest = {
  buildVersion?: unknown;
};

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const UPDATE_TOAST_ID = "tickbird-version-update";
const UPDATE_OPERATION_ID = "versionUpdate";
const PREVIEW_QUERY_PARAM = "previewVersionUpdate";

function getVersionManifestUrl() {
  const url = new URL(`${import.meta.env.BASE_URL}version.json`, window.location.origin);
  url.searchParams.set("t", Date.now().toString());
  return url;
}

async function fetchLatestBuildVersion() {
  const response = await fetch(getVersionManifestUrl(), { cache: "no-store" });

  if (!response.ok) {
    return undefined;
  }

  const manifest = (await response.json()) as VersionManifest;
  return typeof manifest.buildVersion === "string" ? manifest.buildVersion.trim() : undefined;
}

type Translate = ReturnType<typeof useI18n>["t"];

function showVersionUpdateToast(t: Translate) {
  toast.info(t("version.ready"), {
    id: UPDATE_TOAST_ID,
    description: t("version.description"),
    duration: Number.POSITIVE_INFINITY,
    dismissible: false,
    action: (
      <Button size="sm" onClick={() => window.location.reload()}>
        {t("version.refresh")}
      </Button>
    ),
  });
}

/** 生产环境定期检查静态版本清单，并按顶栏是否可见选择提示位置。 */
function VersionUpdateNotifier() {
  const { t } = useI18n();
  const { available, dismiss, show } = useTopbarIsland();
  const hasNotifiedRef = useRef(false);
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    if (!updateReady) {
      return;
    }

    if (available) {
      toast.dismiss(UPDATE_TOAST_ID);
      show(
        "operation",
        {
          confirmLabel: t("version.refresh"),
          description: t("version.description"),
          icon: "line-md:alert-circle-twotone-loop",
          id: UPDATE_OPERATION_ID,
          onConfirm: () => window.location.reload(),
          phase: "prompt",
          title: t("version.ready"),
        },
        { duration: null, priority: "background" },
      );
      return () => dismiss(UPDATE_OPERATION_ID);
    }

    showVersionUpdateToast(t);
    return () => toast.dismiss(UPDATE_TOAST_ID);
  }, [available, dismiss, show, t, updateReady]);

  useEffect(() => {
    const previewRequested = import.meta.env.DEV && new URLSearchParams(window.location.search).get(PREVIEW_QUERY_PARAM) === "1";

    if (hasNotifiedRef.current) {
      return;
    }

    if (previewRequested) {
      hasNotifiedRef.current = true;
      setUpdateReady(true);
      return;
    }

    if (!import.meta.env.PROD) {
      return;
    }

    const currentBuildVersion = import.meta.env.VITE_APP_BUILD_VERSION;
    let disposed = false;
    let checking = false;

    const checkVersion = async () => {
      if (disposed || checking || hasNotifiedRef.current) {
        return;
      }

      checking = true;

      try {
        const latestBuildVersion = await fetchLatestBuildVersion();

        if (disposed || !latestBuildVersion || latestBuildVersion === currentBuildVersion) {
          return;
        }

        hasNotifiedRef.current = true;
        setUpdateReady(true);
      } catch {
        // 短暂的网络或解析错误不影响当前页面，下次轮询时再重试。
      } finally {
        checking = false;
      }
    };

    const requestCheck = () => {
      void checkVersion();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        requestCheck();
      }
    };

    requestCheck();
    const intervalId = window.setInterval(requestCheck, CHECK_INTERVAL_MS);
    window.addEventListener("focus", requestCheck);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", requestCheck);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return null;
}

export default VersionUpdateNotifier;
